<template>
  <div class="bg-gray-50">
    <HeaderItem />

    <!-- ─── HERO ─────────────────────────────────────────────────────── -->
    <section class="hero-section relative overflow-hidden" @touchstart="touchStart" @touchend="touchEnd">

      <!-- Image area: 300px fixed on mobile, full-screen absolute on desktop -->
      <div class="hero-image-area relative overflow-hidden">

        <!-- Slides -->
        <div
          v-for="(slide, index) in slides"
          :key="index"
          class="absolute inset-0 transition-opacity duration-1000"
          :class="currentSlide === index ? 'opacity-100' : 'opacity-0'"
        >
          <div class="absolute inset-0 overflow-hidden">
            <img
              :src="slide.image"
              :alt="slide.alt"
              class="w-full h-full object-cover ken-burns-img"
              :style="{ objectPosition: slide.objectPosition || 'center center' }"
            />
          </div>
          <div class="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/60"></div>
        </div>

        <!-- Hero Content: desktop only -->
        <div class="hidden sm:flex relative z-10 h-full flex-col items-center justify-center text-center px-4 sm:pb-28">
          <div class="max-w-4xl mx-auto">

            <!-- Location badge -->
            <div class="inline-flex items-center gap-2 bg-brand/20 border border-brand/40 text-brand-light text-xs font-semibold tracking-widest px-4 py-2 rounded-full mb-4 md:mb-6">
              <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd" />
              </svg>
              Kigali, Rwanda
            </div>

            <!-- Title -->
            <transition name="slide-fade" mode="out-in">
              <h1 :key="currentSlide" class="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-4 md:mb-6 leading-tight">
                {{ slides[currentSlide].title }}
                <span class="text-brand-light"> {{ slides[currentSlide].highlight }}</span>
              </h1>
            </transition>

            <p class="text-sm sm:text-base md:text-xl text-white/70 mb-6 md:mb-10 max-w-2xl mx-auto leading-relaxed">
              Premium car rental in Kigali — self-drive, 4x4 safari & executive options for every journey
            </p>

            <div class="flex flex-col sm:flex-row gap-4 justify-center">
              <router-link
                to="/car-listing"
                class="bg-brand hover:bg-brand-dark text-white font-bold px-8 py-4 rounded-full transition-all duration-200 shadow-xl shadow-brand/30 hover:shadow-brand/50 hover:-translate-y-0.5 text-base"
              >
                View Our Fleet
              </router-link>
              <a
                :href="whatsappLink"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 text-white font-bold px-8 py-4 rounded-full transition-all duration-200 hover:-translate-y-0.5 text-base"
              >
                <svg class="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                WhatsApp Us
              </a>
            </div>
          </div>
        </div>

        <!-- Mobile: slide title at bottom, like footer fleet card -->
        <div class="sm:hidden absolute bottom-0 left-0 right-0 z-10 px-4 pb-9 pt-20 bg-gradient-to-t from-black/80 via-black/30 to-transparent">
          <transition name="slide-fade" mode="out-in">
            <div :key="currentSlide">
              <p class="text-white font-bold text-base leading-tight">
                {{ slides[currentSlide].title }}<span class="text-brand-light"> {{ slides[currentSlide].highlight }}</span>
              </p>
              <p class="text-white/60 text-xs mt-0.5">Premium Car Hire · Kigali, Rwanda</p>
            </div>
          </transition>
        </div>

        <!-- Slide Dots -->
        <div class="absolute bottom-3 sm:bottom-32 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
          <button
            v-for="(slide, index) in slides"
            :key="index"
            @click="goToSlide(index)"
            class="rounded-full bg-white/40 hover:bg-white/70 transition-all duration-300"
            :class="currentSlide === index ? 'w-8 h-2 bg-brand hover:bg-brand' : 'w-2 h-2'"
          ></button>
        </div>

        <!-- Prev / Next — desktop only -->
        <button
          @click="previousSlide"
          class="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 bg-white/10 hover:bg-brand border border-white/20 hover:border-brand text-white rounded-full items-center justify-center transition-all duration-200 backdrop-blur-sm"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <button
          @click="nextSlide"
          class="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 bg-white/10 hover:bg-brand border border-white/20 hover:border-brand text-white rounded-full items-center justify-center transition-all duration-200 backdrop-blur-sm"
        >
          <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
          </svg>
        </button>

      </div><!-- /hero-image-area -->

      <!-- Booking Bar: flows below image on mobile, absolute at bottom on desktop -->
      <div class="hero-booking-bar bg-[#0F1117] border-t border-white/10 z-10">
        <div class="max-w-5xl mx-auto px-4 py-4">
          <form @submit.prevent="quickBook" class="grid grid-cols-2 sm:flex sm:flex-row gap-3 sm:items-center sm:flex-wrap">
            <select
              v-model="quickBookForm.carType"
              class="col-span-1 sm:flex-1 sm:min-w-36 bg-white/10 border border-white/20 text-white text-sm px-4 py-3 rounded-xl focus:outline-none focus:border-brand transition-all"
            >
              <option value="" class="text-gray-900">Select Car Type</option>
              <option value="Toyota RAV4" class="text-gray-900">Toyota RAV4</option>
              <option value="Safari 4x4" class="text-gray-900">Safari 4x4</option>
              <option value="Land Cruiser Prado" class="text-gray-900">Land Cruiser Prado</option>
              <option value="Toyota Hiace Minibus" class="text-gray-900">Minibus (Hiace)</option>
              <option value="Toyota Coaster" class="text-gray-900">Coaster (29 seats)</option>
              <option value="Toyota Highlander" class="text-gray-900">Toyota Highlander</option>
            </select>
            <input
              v-model="quickBookForm.pickUp"
              type="text"
              placeholder="Pick-up location"
              class="col-span-1 sm:flex-1 sm:min-w-36 bg-white/10 border border-white/20 text-white placeholder-white/40 text-sm px-4 py-3 rounded-xl focus:outline-none focus:border-brand transition-all"
            />
            <input
              v-model="quickBookForm.pickupDate"
              type="date"
              :min="today"
              class="col-span-1 sm:flex-1 sm:min-w-36 bg-white/10 border border-white/20 text-white/80 text-sm px-4 py-3 rounded-xl focus:outline-none focus:border-brand transition-all"
            />
            <input
              v-model="quickBookForm.returnDate"
              type="date"
              :min="quickBookForm.pickupDate || today"
              class="col-span-1 sm:flex-1 sm:min-w-36 bg-white/10 border border-white/20 text-white/80 text-sm px-4 py-3 rounded-xl focus:outline-none focus:border-brand transition-all"
            />
            <button
              type="submit"
              class="col-span-2 sm:col-span-auto sm:flex-shrink-0 bg-brand hover:bg-brand-dark text-white font-bold text-sm px-6 py-3 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-lg shadow-brand/20"
            >
              <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              Book via WhatsApp
            </button>
          </form>
        </div>
      </div>

    </section>

    <!-- ─── STATS BAR ──────────────────────────────────────────────────── -->
    <section class="bg-[#0F1117] py-12 px-4">
      <div class="max-w-5xl mx-auto">
        <div class="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div v-for="stat in stats" :key="stat.label" class="text-center">
            <div class="text-3xl md:text-4xl font-bold text-brand-light mb-1">{{ stat.value }}</div>
            <div class="text-white/50 text-sm font-medium">{{ stat.label }}</div>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── USE CASES ───────────────────────────────────────────────────── -->
    <section class="py-20 px-4 bg-white">
      <div class="max-w-7xl mx-auto">
        <div class="text-center mb-12">
          <span class="text-brand text-xs font-bold tracking-widest uppercase block mb-2">Plan Your Journey</span>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mb-4">What Brings You to Rwanda?</h2>
          <p class="text-gray-500 max-w-xl mx-auto text-sm leading-relaxed">
            Whether it's a safari adventure, airport transfer or group trip — we have the right vehicle for every journey.
          </p>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <a
            v-for="useCase in useCases"
            :key="useCase.title"
            :href="useCase.whatsapp"
            target="_blank"
            rel="noopener noreferrer"
            class="group relative overflow-hidden rounded-2xl border border-gray-200 shadow-sm hover:border-brand/30 hover:shadow-xl hover:shadow-brand/5 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
          >
            <!-- Background image -->
            <div class="relative overflow-hidden" style="height: 200px;">
              <img :src="useCase.image" :alt="useCase.title" class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" :style="{ objectPosition: useCase.imgPosition || 'center center' }" />
              <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
              <!-- Icon badge -->
              <div class="absolute top-3 left-3 w-10 h-10 bg-brand rounded-xl flex items-center justify-center text-xl shadow-lg">
                {{ useCase.icon }}
              </div>
              <!-- Car type pill -->
              <span class="absolute top-3 right-3 bg-white/20 backdrop-blur-sm border border-white/30 text-white text-xs font-semibold px-2.5 py-1 rounded-full">
                {{ useCase.carType }}
              </span>
            </div>
            <div class="p-5 bg-white">
              <h3 class="font-bold text-gray-900 mb-1">{{ useCase.title }}</h3>
              <p class="text-gray-500 text-xs leading-relaxed mb-4">{{ useCase.description }}</p>
              <div class="flex items-center gap-1 text-brand font-semibold text-xs group-hover:gap-2 transition-all duration-200">
                Book This Trip
                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </div>
            </div>
          </a>
        </div>
      </div>
    </section>

    <!-- ─── TRUST STRIP ──────────────────────────────────────────────────── -->
    <section class="bg-[#0F1117] py-10 px-4">
      <div class="max-w-7xl mx-auto">
        <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
          <div
            v-for="trust in trustBadges"
            :key="trust.label"
            class="flex items-center gap-3"
          >
            <div class="w-9 h-9 bg-brand/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5 text-brand-light" fill="currentColor" :viewBox="trust.viewBox" v-html="trust.icon"></svg>
            </div>
            <span class="text-white/70 text-xs leading-snug font-medium">{{ trust.label }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── FEATURES ──────────────────────────────────────────────────── -->
    <section class="py-20 px-4 bg-white">
      <div class="max-w-6xl mx-auto">
        <div class="text-center mb-14">
          <span class="text-brand text-xs font-bold tracking-widest uppercase">Why Choose Us</span>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mt-3 mb-4">
            Everything You Need <span class="text-brand">for Rwanda</span>
          </h2>
          <p class="text-gray-500 max-w-2xl mx-auto text-sm leading-relaxed">
            We make car rental in Kigali simple, affordable, and stress-free — whether you're here for safari, business, or leisure.
          </p>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">

          <!-- Feature 1 -->
          <div class="group p-8 rounded-2xl border border-gray-100 hover:border-brand/30 hover:shadow-xl transition-all duration-300">
            <div class="w-14 h-14 bg-brand/10 group-hover:bg-brand rounded-2xl flex items-center justify-center mb-6 transition-colors duration-300">
              <svg class="w-7 h-7 text-brand group-hover:text-white transition-colors duration-300" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-4.908 4.908A1 1 0 005.092 11H5a1 1 0 100 2h.092a4.535 4.535 0 004.908 4.908V17a1 1 0 102 0v-.092a4.535 4.535 0 004.908-4.908A1 1 0 0014.908 11H15a1 1 0 100-2h-.092a4.535 4.535 0 00-4.908-4.908V5z" clip-rule="evenodd" />
              </svg>
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-3">Affordable Prices</h3>
            <p class="text-gray-500 leading-relaxed text-sm">Transparent pricing with no hidden fees. Competitive daily rates for every budget and trip type.</p>
          </div>

          <!-- Feature 2 -->
          <div class="group p-8 rounded-2xl border border-gray-100 hover:border-brand/30 hover:shadow-xl transition-all duration-300">
            <div class="w-14 h-14 bg-brand/10 group-hover:bg-brand rounded-2xl flex items-center justify-center mb-6 transition-colors duration-300">
              <svg class="w-7 h-7 text-brand group-hover:text-white transition-colors duration-300" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
              </svg>
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-3">24/7 Support</h3>
            <p class="text-gray-500 leading-relaxed text-sm">Our team is always reachable via WhatsApp or phone — before, during, and after your trip across Rwanda.</p>
          </div>

          <!-- Feature 3 -->
          <div class="group p-8 rounded-2xl border border-gray-100 hover:border-brand/30 hover:shadow-xl transition-all duration-300">
            <div class="w-14 h-14 bg-brand/10 group-hover:bg-brand rounded-2xl flex items-center justify-center mb-6 transition-colors duration-300">
              <svg class="w-7 h-7 text-brand group-hover:text-white transition-colors duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
              </svg>
            </div>
            <h3 class="text-xl font-bold text-gray-900 mb-3">Flexible Options</h3>
            <p class="text-gray-500 leading-relaxed text-sm">Self-drive, with driver, airport pickup, or safari packages — we adapt to your schedule and needs.</p>
          </div>

        </div>
      </div>
    </section>

    <!-- ─── FLEET PREVIEW ───────────────────────────────────────────────── -->
    <section class="py-20 px-4 bg-gray-50">
      <div class="max-w-7xl mx-auto">

        <!-- Header row -->
        <div class="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-12">
          <div>
            <span class="text-brand text-xs font-bold tracking-widest uppercase block mb-2">Available Now</span>
            <h2 class="text-3xl md:text-4xl font-bold text-gray-900">Our Fleet</h2>
          </div>
          <router-link
            to="/car-listing"
            class="inline-flex items-center gap-2 text-brand hover:text-brand-dark font-semibold text-sm transition-colors duration-200"
          >
            View All Vehicles
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </router-link>
        </div>

        <!-- Cars grid -->
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
          <div
            v-for="car in fleet"
            :key="car.id"
            class="group bg-white rounded-2xl overflow-hidden border border-gray-200 shadow-sm hover:border-brand/30 hover:shadow-xl hover:shadow-brand/5 transition-all duration-300 hover:-translate-y-1 flex flex-col"
          >
            <!-- Media -->
            <div class="relative overflow-hidden" style="height: 340px;">
              <video
                v-if="car.media.type === 'video'"
                autoplay
                muted
                loop
                playsinline
                class="w-full h-full transition-transform duration-700 group-hover:scale-105"
                :class="car.media.portrait ? 'object-contain bg-[#0F1117]' : 'object-cover'"
              >
                <source :src="car.media.src" type="video/mp4" />
              </video>
              <img
                v-else
                :src="car.media.src"
                :alt="car.name"
                class="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                :class="car.media.portrait ? 'object-center' : 'object-center'"
                :style="car.media.portrait ? 'object-position: center 30%' : ''"
              />
              <div class="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
              <span class="absolute top-3 left-3 bg-brand text-white text-xs font-bold px-3 py-1 rounded-full">
                {{ car.type }}
              </span>
              <span v-if="car.badge" class="absolute top-3 right-3 bg-white text-brand text-xs font-bold px-2.5 py-1 rounded-full shadow">
                {{ car.badge }}
              </span>
            </div>

            <!-- Card body -->
            <div class="p-5 flex flex-col flex-1">
              <h3 class="text-base font-bold text-gray-900 mb-1">{{ car.name }}</h3>
              <p v-if="car.year" class="text-xs text-gray-400 mb-3">{{ car.year }} Model</p>

              <div class="flex flex-wrap items-center gap-3 mb-4 mt-auto">
                <div class="flex items-center gap-1.5 text-gray-500 text-xs">
                  <svg class="w-3.5 h-3.5 text-brand flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                  </svg>
                  <span class="font-medium text-gray-700">{{ car.seats }} Seats</span>
                </div>
                <div class="flex items-center gap-1.5 text-gray-500 text-xs">
                  <svg class="w-3.5 h-3.5 text-brand flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clip-rule="evenodd" />
                  </svg>
                  <span class="font-medium text-gray-700">{{ car.transmission }}</span>
                </div>
                <div class="flex items-center gap-1.5 text-gray-500 text-xs">
                  <svg class="w-3.5 h-3.5 text-brand flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M5 2a1 1 0 011 1v1h1a1 1 0 010 2H6v1a1 1 0 01-2 0V6H3a1 1 0 010-2h1V3a1 1 0 011-1zm0 10a1 1 0 011 1v1h1a1 1 0 110 2H6v1a1 1 0 11-2 0v-1H3a1 1 0 110-2h1v-1a1 1 0 011-1zM12 2a1 1 0 01.967.744L14.146 7.2 17.5 9.134a1 1 0 010 1.732l-3.354 1.935-1.18 4.455a1 1 0 01-1.933 0L9.854 12.8 6.5 10.866a1 1 0 010-1.732l3.354-1.935 1.18-4.455A1 1 0 0112 2z" clip-rule="evenodd" />
                  </svg>
                  <span class="font-medium text-gray-700">{{ car.fuel }}</span>
                </div>
              </div>

              <button
                @click="bookFleetCar(car)"
                class="w-full bg-brand hover:bg-brand-dark text-white font-bold text-sm py-2.5 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-sm shadow-brand/20"
              >
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                </svg>
                Book via WhatsApp
              </button>
            </div>
          </div>
        </div>

      </div>
    </section>

    <!-- ─── HOW IT WORKS ────────────────────────────────────────────────── -->
    <section class="py-20 px-4 bg-[#0F1117]">
      <div class="max-w-7xl mx-auto">
        <div class="text-center mb-14">
          <span class="text-brand text-xs font-bold tracking-widest uppercase block mb-2">Simple Process</span>
          <h2 class="text-3xl md:text-4xl font-bold text-white mb-4">Book a Car in 4 Easy Steps</h2>
          <p class="text-white/50 max-w-xl mx-auto text-sm leading-relaxed">
            No complicated forms. No waiting days for a reply. We confirm your booking within minutes on WhatsApp.
          </p>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          <div
            v-for="(step, index) in howItWorks"
            :key="step.title"
            class="relative"
          >
            <!-- Connector line (desktop) -->
            <div v-if="index < 3" class="hidden lg:block absolute top-8 left-full w-full h-px bg-brand/30 z-0" style="width: calc(100% - 2rem); left: calc(100% - 1rem);"></div>
            <div class="relative z-10">
              <!-- Step number circle -->
              <div class="w-16 h-16 bg-brand/10 border border-brand/30 rounded-2xl flex items-center justify-center mb-5">
                <svg class="w-7 h-7 text-brand-light" fill="currentColor" :viewBox="step.viewBox" v-html="step.icon"></svg>
              </div>
              <div class="text-brand text-xs font-bold tracking-widest uppercase mb-2">Step {{ index + 1 }}</div>
              <h3 class="text-white font-bold text-lg mb-2">{{ step.title }}</h3>
              <p class="text-white/50 text-sm leading-relaxed">{{ step.description }}</p>
            </div>
          </div>
        </div>
        <div class="text-center mt-12">
          <a
            :href="whatsappLink"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-2 bg-brand hover:bg-brand-dark text-white font-bold px-8 py-4 rounded-full transition-all duration-200 shadow-xl shadow-brand/30 hover:-translate-y-0.5"
          >
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            Start Your Booking Now
          </a>
        </div>
      </div>
    </section>

    <!-- ─── TESTIMONIALS ──────────────────────────────────────────────── -->
    <section class="py-20 px-4 bg-white">
      <div class="max-w-6xl mx-auto">
        <div class="text-center mb-14">
          <span class="text-brand text-xs font-bold tracking-widest uppercase">Happy Clients</span>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mt-3">
            What Travelers <span class="text-brand">Say About Us</span>
          </h2>
        </div>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div
            v-for="review in reviews"
            :key="review.name"
            class="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-shadow duration-300 flex flex-col"
          >
            <div class="text-brand-light text-lg mb-4">★★★★★</div>
            <p class="text-gray-600 leading-relaxed flex-1 mb-6 text-sm">"{{ review.text }}"</p>
            <div class="flex items-center gap-3 pt-4 border-t border-gray-100">
              <img :src="review.image" :alt="review.name" class="w-12 h-12 rounded-full object-cover flex-shrink-0" />
              <div>
                <div class="font-semibold text-gray-900 text-sm">{{ review.name }}</div>
                <div class="text-gray-400 text-xs flex items-center gap-1 mt-0.5">
                  <span>{{ review.flag }}</span>
                  <span>{{ review.location }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ─── FAQ ───────────────────────────────────────────────────────── -->
    <section class="py-20 px-4 bg-white">
      <div class="max-w-3xl mx-auto">
        <div class="text-center mb-14">
          <span class="text-brand text-xs font-bold tracking-widest uppercase">FAQs</span>
          <h2 class="text-3xl md:text-4xl font-bold text-gray-900 mt-3">Common Questions</h2>
        </div>
        <div class="space-y-3">
          <div
            v-for="(faq, index) in faqs"
            :key="index"
            class="border border-gray-200 rounded-xl overflow-hidden"
          >
            <button
              @click="toggleFaq(index)"
              class="w-full flex items-center justify-between px-6 py-5 text-left hover:bg-gray-50 transition-colors duration-200"
            >
              <span class="font-semibold text-gray-900 text-sm md:text-base pr-4">{{ faq.question }}</span>
              <svg
                class="w-5 h-5 text-brand flex-shrink-0 transition-transform duration-200"
                :class="openFaq === index ? 'rotate-180' : ''"
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div v-show="openFaq === index" class="px-6 pb-5">
              <p class="text-gray-500 leading-relaxed text-sm">{{ faq.answer }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <FooterItem />
  </div>
</template>

<script>
import HeaderItem from '../components/HeaderItem.vue'
import FooterItem from '../components/FooterItem.vue'

export default {
  name: 'HomeView',
  components: { HeaderItem, FooterItem },
  data () {
    return {
      currentSlide: 0,
      autoplayInterval: null,
      touchStartX: 0,
      openFaq: null,
      quickBookForm: { carType: '', pickUp: '', pickupDate: '', returnDate: '' },
      slides: [
        {
          image: 'img/toyota-rav4-2021-black.jpeg',
          alt: 'Toyota RAV4 2021 car hire Kigali Rwanda',
          title: 'Explore Rwanda',
          highlight: 'in Style',
          objectPosition: '35% 55%'
        },
        {
          image: 'img/toyota-fortuner-white.jpeg',
          alt: 'Toyota Fortuner safari car hire Rwanda',
          title: 'Safari Adventures',
          highlight: 'Start Here',
          objectPosition: 'center 55%'
        },
        {
          image: 'img/nissan-patrol-v8-kigali.jpeg',
          alt: 'Nissan Patrol V8 luxury car hire Kigali',
          title: 'Premium Luxury',
          highlight: 'Experience',
          objectPosition: '30% 50%'
        },
        {
          image: 'img/toyota-hiace-grand-cabin.jpeg',
          alt: 'Toyota Hiace Grand Cabin group hire Rwanda',
          title: 'Group Travel',
          highlight: 'Made Easy',
          objectPosition: 'center 60%'
        }
      ],
      useCases: [
        {
          title: 'Gorilla Trekking',
          description: "Volcanoes National Park requires a capable 4x4. We'll get you there and back safely.",
          icon: '🦍',
          carType: '4x4 Safari',
          image: 'img/nissan-patrol-v8-kigali.jpeg',
          imgPosition: '30% 50%',
          whatsapp: 'https://wa.me/250782336854?text=Hi!%20I%20need%20a%204x4%20vehicle%20for%20gorilla%20trekking%20in%20Rwanda.%20Please%20confirm%20availability%20and%20details.'
        },
        {
          title: 'Airport Transfer',
          description: 'Smooth pickup or drop-off at Kigali International Airport — on time, every time.',
          icon: '✈️',
          carType: 'SUV',
          image: 'img/toyota-rav4-2021-black.jpeg',
          imgPosition: '35% 55%',
          whatsapp: 'https://wa.me/250782336854?text=Hi!%20I%20need%20an%20airport%20transfer%20in%20Kigali.%20Please%20send%20me%20more%20details.'
        },
        {
          title: 'Business Travel',
          description: 'Clean, well-maintained executive vehicles for corporate trips across Kigali.',
          icon: '💼',
          carType: 'Executive',
          image: 'img/toyota-fortuner-white.jpeg',
          imgPosition: 'center 55%',
          whatsapp: 'https://wa.me/250782336854?text=Hi!%20I%20need%20an%20executive%20vehicle%20for%20business%20travel%20in%20Kigali.%20Please%20send%20details.'
        },
        {
          title: 'Group Travel',
          description: 'Travelling with a team, tour group or family? Our minibuses seat up to 29 passengers.',
          icon: '👥',
          carType: 'Minibus',
          image: 'img/toyota-hiace-grand-cabin.jpeg',
          imgPosition: 'center 60%',
          whatsapp: 'https://wa.me/250782336854?text=Hi!%20I%20need%20a%20minibus%20for%20group%20travel%20in%20Rwanda.%20Please%20confirm%20availability.'
        }
      ],
      trustBadges: [
        { label: 'Google Verified Business', viewBox: '0 0 20 20', icon: '<path fill-rule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />' },
        { label: 'Airport Pickup Included', viewBox: '0 0 20 20', icon: '<path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />' },
        { label: 'International Licence Accepted', viewBox: '0 0 20 20', icon: '<path fill-rule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clip-rule="evenodd" />' },
        { label: 'Replacement Car if Breakdown', viewBox: '0 0 20 20', icon: '<path fill-rule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clip-rule="evenodd" />' },
        { label: '500+ Happy Clients Worldwide', viewBox: '0 0 20 20', icon: '<path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />' }
      ],
      howItWorks: [
        {
          title: 'Choose Your Vehicle',
          description: 'Browse our fleet and pick the car that fits your trip — SUV, 4x4 safari, minibus or executive.',
          viewBox: '0 0 20 20',
          icon: '<path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" /><path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1V5a1 1 0 00-1-1H3zM14 7a1 1 0 00-1 1v6.05A2.5 2.5 0 0115.95 16H17a1 1 0 001-1v-5a1 1 0 00-.293-.707l-2-2A1 1 0 0015 7h-1z" />'
        },
        {
          title: 'Send Your Dates',
          description: 'Use the booking bar above or WhatsApp us your pick-up date, return date and location.',
          viewBox: '0 0 20 20',
          icon: '<path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd" />'
        },
        {
          title: 'We Confirm Instantly',
          description: 'Our team replies on WhatsApp within minutes to confirm availability and arrange everything.',
          viewBox: '0 0 20 20',
          icon: '<path fill-rule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clip-rule="evenodd" />'
        },
        {
          title: 'We Deliver to You',
          description: 'Pick up from our office, your hotel, or Kigali Airport — we come to you at no extra charge.',
          viewBox: '0 0 20 20',
          icon: '<path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd" />'
        }
      ],
      fleet: [
        { id: 1, name: 'Toyota RAV4', year: '2021', type: 'SUV', seats: 7, transmission: 'Automatic', fuel: 'Petrol', badge: 'Most Popular', media: { type: 'video', src: 'img/rwanda-carhire.mp4' } },
        { id: 2, name: 'Toyota RAV4', year: '2018', type: 'SUV', seats: 5, transmission: 'Automatic', fuel: 'Petrol', badge: '', media: { type: 'image', src: 'img/toyota-rav4-2018-black.jpeg' } },
        { id: 3, name: 'Toyota Fortuner', year: '2015', type: 'SUV', seats: 7, transmission: 'Automatic', fuel: 'Petrol', badge: '', media: { type: 'image', src: 'img/toyota-fortuner-white.jpeg' } },
        { id: 4, name: 'Toyota Coaster', year: '', type: 'Minibus', seats: 29, transmission: 'Manual', fuel: 'Diesel', badge: 'Group Travel', media: { type: 'video', src: 'img/coastel.mp4' } },
        { id: 5, name: 'Land Cruiser 200 VXR', year: '2018', type: '4x4 Safari', seats: 8, transmission: 'Automatic', fuel: 'Diesel', badge: 'Safari Ready', media: { type: 'image', src: 'img/land-cruiser-200-vxr-front.jpeg', portrait: true } },
        { id: 6, name: 'Safari 4x4', year: '', type: '4x4 Safari', seats: 5, transmission: 'Manual', fuel: 'Diesel', badge: 'Safari Ready', media: { type: 'video', src: 'img/safar-carvideo.mp4' } },
        { id: 7, name: 'Toyota RAV4', year: '2021', type: 'SUV', seats: 5, transmission: 'Automatic', fuel: 'Petrol', badge: '', media: { type: 'image', src: 'img/toyota-rav4-2021-black.jpeg' } },
        { id: 8, name: 'Toyota Hiace Grand Cabin', year: '2022', type: 'Minibus', seats: 14, transmission: 'Manual', fuel: 'Diesel', badge: '', media: { type: 'image', src: 'img/toyota-hiace-grand-cabin.jpeg' } },
        { id: 9, name: 'Nissan Patrol V8', year: '2020', type: 'Luxury SUV', seats: 8, transmission: 'Automatic', fuel: 'Petrol', badge: 'Premium', media: { type: 'image', src: 'img/nissan-patrol-v8-kigali.jpeg' } },
        { id: 10, name: 'SUV Tour', year: '', type: 'SUV', seats: 5, transmission: 'Automatic', fuel: 'Petrol', badge: 'New', media: { type: 'video', src: 'img/suv-tour-kigali-portrait.mp4', portrait: true } }
      ],
      stats: [
        { value: '10+', label: 'Years Experience' },
        { value: '50+', label: 'Vehicles Available' },
        { value: '500+', label: 'Happy Clients' },
        { value: '24/7', label: 'Customer Support' }
      ],
      reviews: [
        {
          name: 'Ganza Tamba Heritier',
          flag: '🇷🇼',
          location: 'Kigali, Rwanda',
          image: 'img/tambapic.jpeg',
          text: 'This company has the best service — they care about your experience, not just your money. The car was clean and fully functional. Highly recommend!'
        },
        {
          name: 'Tierarzt Röhl',
          flag: '🇩🇪',
          location: 'Germany',
          image: 'img/tierarzt.png',
          text: 'A lot of experiences and the possibility to build beautiful memories. The hired RAV4 is highly recommended for a Rwanda visit.'
        },
        {
          name: 'Marek Soomlais',
          flag: '🇪🇪',
          location: 'Estonia',
          image: 'img/marek.png',
          text: 'It was a great experience. The car was delivered to my hotel and when I left the country, I returned it at the airport. Very convenient!'
        }
      ],
      faqs: [
        {
          question: 'How can I make a reservation?',
          answer: 'Fill out the quick booking bar above or contact us directly via WhatsApp (+250 782 336 854). We confirm within minutes.'
        },
        {
          question: 'Do you offer airport pickup and drop-off?',
          answer: 'Yes! We provide Kigali International Airport pickup and drop-off. Just include your flight details when booking.'
        },
        {
          question: 'What documents do I need to rent?',
          answer: 'A valid driver\'s license (international licenses accepted), passport, and a credit or debit card. That\'s it.'
        },
        {
          question: 'What is your fuel policy?',
          answer: 'Full-to-full. We provide the vehicle with a full tank and ask that it is returned with the same fuel level.'
        }
      ]
    }
  },
  computed: {
    today () {
      return new Date().toISOString().split('T')[0]
    },
    whatsappLink () {
      const phone = '250782336854'
      const msg = encodeURIComponent('Hello! I\'d like to book a car rental with Kigali Rwanda Car Hire. Please send me more details.')
      return `https://wa.me/${phone}?text=${msg}`
    }
  },
  mounted () {
    this.startAutoplay()
  },
  beforeUnmount () {
    this.stopAutoplay()
  },
  metaInfo () {
    return {
      title: 'Kigali Rwanda Car Hire | (+250) 782 336 854 | Car Rental Kigali',
      meta: [
        { name: 'description', content: 'Rent a car in Kigali with Kigali Rwanda Car Hire. Affordable self-drive, 4x4 safari, minibus & executive car rental in Rwanda. Airport pickup available. Book now: (+250) 782 336 854' },
        { name: 'keywords', content: 'car rental Kigali, Rwanda car hire, Kigali car rental, rent a car Rwanda, Kigali car rental self drive, 4x4 car hire Rwanda, cheap car rental Kigali, Kigali airport car hire, Rwanda car rental with driver, safari car rental Rwanda, Toyota RAV4 hire Rwanda, car hire Kigali Rwanda, affordable car rental Kigali, gorilla trekking car hire Rwanda, Rwanda safari vehicle hire' },
        { property: 'og:title', content: 'Kigali Rwanda Car Hire | Car Rental Kigali | (+250) 782 336 854' },
        { property: 'og:description', content: 'Affordable car rental in Kigali — self-drive, 4x4 safari, minibus & executive vehicles. Airport pickup available. Book via WhatsApp.' },
        { property: 'og:url', content: 'https://kigalirwandacarhire.com/' },
        { property: 'og:image', content: 'https://kigalirwandacarhire.com/img/toyota-rav4-2021-black.jpeg' }
      ]
    }
  },
  methods: {
    touchStart (e) {
      this.touchStartX = e.changedTouches[0].screenX
    },
    touchEnd (e) {
      const diff = this.touchStartX - e.changedTouches[0].screenX
      if (Math.abs(diff) > 50) {
        diff > 0 ? this.nextSlide() : this.previousSlide()
      }
    },
    nextSlide () {
      this.currentSlide = (this.currentSlide + 1) % this.slides.length
      this.resetAutoplay()
    },
    previousSlide () {
      this.currentSlide = (this.currentSlide - 1 + this.slides.length) % this.slides.length
      this.resetAutoplay()
    },
    goToSlide (index) {
      this.currentSlide = index
      this.resetAutoplay()
    },
    startAutoplay () {
      this.autoplayInterval = setInterval(() => { this.nextSlide() }, 6000)
    },
    stopAutoplay () {
      if (this.autoplayInterval) {
        clearInterval(this.autoplayInterval)
        this.autoplayInterval = null
      }
    },
    resetAutoplay () {
      this.stopAutoplay()
      this.startAutoplay()
    },
    toggleFaq (index) {
      this.openFaq = this.openFaq === index ? null : index
    },
    bookFleetCar (car) {
      const phone = '250782336854'
      const msg = `Car Rental Request — kigalirwandacarhire.com\n---\nCar: ${car.name}${car.year ? ' ' + car.year : ''}\nType: ${car.type}\nSeats: ${car.seats}\nTransmission: ${car.transmission}\nFuel: ${car.fuel}\n\nPlease confirm availability.`
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank')
    },
    quickBook () {
      const phone = '250782336854'
      const carType = this.quickBookForm.carType || 'Not specified'
      const pickUp = this.quickBookForm.pickUp || 'Not specified'
      const pickupDate = this.quickBookForm.pickupDate || 'Not specified'
      const returnDate = this.quickBookForm.returnDate || 'Not specified'
      const msg = `Car Rental Request — kigalirwandacarhire.com\n---\nCar Type: ${carType}\nPick-up Location: ${pickUp}\nPick-up Date: ${pickupDate}\nReturn Date: ${returnDate}\n\nPlease confirm availability.`
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank')
    }
  }
}
</script>

<style scoped>
/* Mobile: image area is a fixed-height card, booking bar flows below */
.hero-image-area {
  height: 300px;
}

/* Desktop: hero section is full screen, image area fills it, booking bar is pinned to bottom */
@media (min-width: 640px) {
  .hero-section {
    height: 100vh;
    height: 100svh;
    min-height: 568px;
  }
  .hero-image-area {
    position: absolute;
    inset: 0;
    height: auto;
  }
  .hero-booking-bar {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: rgba(15, 17, 23, 0.9);
    backdrop-filter: blur(12px);
  }
}

@keyframes kenBurns {
  from { transform: scale(1.0) translate(0%, 0%); }
  to   { transform: scale(1.12) translate(-2%, -1%); }
}

.ken-burns-img {
  animation: kenBurns 12s ease-in-out infinite alternate;
}

@media (max-width: 639px) {
  .ken-burns-img {
    animation: none;
  }
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: opacity 0.5s ease, transform 0.5s ease;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateY(12px);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-12px);
}
</style>
